import { PLAN_ID, type SEOReportPlanId } from "@/lib/seo-intents";

export const REPORT_CHECKOUT_RECOVERY_STORAGE_KEY = "ishvaram_report_checkout_recovery_v1";
const SUPPORT_EMAIL = "support@ishvaram.com";

export interface ReportCheckoutRecoveryContext {
  planId: SEOReportPlanId;
  priceInr: number;
  razorpayOrderId?: string;
  source?: string;
  intent?: string;
  sourcePath?: string;
  customerProblem?: string;
  resumeHref?: string;
  createdAt: string;
}

const REPORT_LABELS: Record<SEOReportPlanId, string> = {
  [PLAN_ID.brihat_kundali]: "Brihat Kundali",
  [PLAN_ID.marriage_report]: "Marriage Report",
  [PLAN_ID.career_report]: "Career Report",
  [PLAN_ID.annual_forecast]: "Annual Forecast",
  [PLAN_ID.sade_sati]: "Sade Sati Report",
  [PLAN_ID.pitra_dosh]: "Pitra Dosh Report",
  [PLAN_ID.mahadasha_forecast]: "Mahadasha Forecast",
  [PLAN_ID.muhurat_report]: "Muhurat Report",
  [PLAN_ID.dosha_remedy_plan]: "Dosha Remedy Plan",
};

const PROBLEM_LABELS: Record<string, string> = {
  chart_report: "Full chart reading",
  daily_guidance: "Daily guidance",
  default_brihat: "Full chart reading",
  dosha_kaal_sarp: "Kaal Sarp Dosh",
  dosha_mangal: "Mangal Dosh",
  dosha_pitra: "Pitra Dosh",
  dosha_remedy: "Dosha remedy",
  dosha_sade_sati: "Shani / Sade Sati",
  festival_ritual: "Festival ritual",
  kaal_sarp: "Kaal Sarp Dosh",
  kaal_sarp_dosh: "Kaal Sarp Dosh",
  mangal_dosh: "Mangal Dosh",
  mangal_dosha: "Mangal Dosh",
  marriage: "Marriage matching",
  next_report_param: "Full chart reading",
  pitra_dosh: "Pitra Dosh",
  remedy_dosha: "Dosha remedy",
  sade_sati: "Shani / Sade Sati",
  shani_dosh: "Shani / Sade Sati",
  timing_muhurat: "Muhurat timing",
};

export function buildReportCheckoutRecoveryContext({
  planId,
  priceInr,
  razorpayOrderId,
  source,
  intent,
  sourcePath,
  customerProblem,
  resumeHref,
}: Omit<ReportCheckoutRecoveryContext, "createdAt">): ReportCheckoutRecoveryContext {
  return {
    planId,
    priceInr,
    razorpayOrderId,
    source,
    intent,
    sourcePath,
    customerProblem,
    resumeHref: resumeHref ?? buildReportRecoveryResumeHref({ planId, sourcePath }),
    createdAt: new Date().toISOString(),
  };
}

export function saveReportCheckoutRecoveryContext(context: ReportCheckoutRecoveryContext) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(REPORT_CHECKOUT_RECOVERY_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // On-page recovery still works when storage is unavailable.
  }
}

export function clearReportCheckoutRecoveryContext() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(REPORT_CHECKOUT_RECOVERY_STORAGE_KEY);
  } catch {
    // Storage failure should not block checkout success.
  }
}

export function buildReportRecoverySupportEmailHref(context: ReportCheckoutRecoveryContext) {
  const report = customerReportLabel(context.planId);
  const problem = customerProblemLabel(context.customerProblem || context.intent);
  const subject = `Ishvaram checkout help: ${report}`;
  const body = [
    "I was checking my Ishvaram report but did not finish checkout.",
    `Report: ${report}`,
    `Problem: ${problem}`,
    context.razorpayOrderId ? `Order: ${context.razorpayOrderId}` : "",
    context.sourcePath ? `Page: ${context.sourcePath}` : "",
    context.resumeHref ? `Resume: ${context.resumeHref}` : "",
    "Please help me continue this report checkout.",
  ]
    .filter(Boolean)
    .join("\n");

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
    body
  )}`;
}

export function buildReportRecoveryResumeHref({
  planId,
  sourcePath,
  origin,
}: {
  planId: SEOReportPlanId;
  sourcePath?: string;
  origin?: string;
}): string {
  const baseOrigin =
    origin ||
    (typeof window !== "undefined" && window.location.origin
      ? window.location.origin
      : "https://ishvaram.com");
  const path = sourcePath && sourcePath.startsWith("/") ? sourcePath : "/free-kundali/";
  const url = new URL(path, baseOrigin);
  url.searchParams.set("next_report", planId);
  url.searchParams.set("checkout_recovery", "1");
  url.searchParams.set("recovery_channel", "support_email");
  return url.href;
}

function customerReportLabel(planId: SEOReportPlanId): string {
  return REPORT_LABELS[planId] ?? "Personalized Report";
}

function customerProblemLabel(value?: string): string {
  if (!value) return "My chart";
  const fallback = value.trim();
  return PROBLEM_LABELS[value] ?? (fallback || "My chart");
}
